/* Layout Manager — resize handles, collapse/expand, localStorage persistence */

import { state, refs } from './state.js';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const STORAGE_KEY = 'agentcpq_layout_v2';

// One-time cleanup: drop obsolete v1 layout so it doesn't sit in storage forever
try { localStorage.removeItem('agentcpq_layout_v1'); } catch { /* ignore */ }

const DEFAULT_LAYOUT = {
  version: 2,
  zones: {
    left:   { width: 260, collapsed: false },
    right:  { width: 300, collapsed: false },
    bottom: { height: 0,  collapsed: true  },
  },
};

// Resize constraints
const LIMITS = {
  left:   { min: 180, max: 500 },
  right:  { min: 200, max: 600 },
  bottom: { min: 80,  maxVhFraction: 0.5 },  // max = 50vh
};

// CSS custom property names
const CSS_PROP = {
  left:   '--zone-left-w',
  right:  '--zone-right-w',
  bottom: '--zone-bottom-h',
};

// ---------------------------------------------------------------------------
// Persistence helpers
// ---------------------------------------------------------------------------

function _loadLayout() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed && parsed.version === DEFAULT_LAYOUT.version && parsed.zones) {
      return parsed;
    }
  } catch { /* corrupt — fall through */ }
  return null;
}

function _saveLayout() {
  const root = document.documentElement;
  const layout = {
    version: DEFAULT_LAYOUT.version,
    zones: {
      left: {
        width:     parseFloat(root.style.getPropertyValue(CSS_PROP.left))  || DEFAULT_LAYOUT.zones.left.width,
        collapsed: !state.leftZoneVisible,
      },
      right: {
        width:     parseFloat(root.style.getPropertyValue(CSS_PROP.right)) || DEFAULT_LAYOUT.zones.right.width,
        collapsed: !state.rightZoneVisible,
      },
      bottom: {
        height:    parseFloat(root.style.getPropertyValue(CSS_PROP.bottom)) || 0,
        collapsed: !state.bottomZoneVisible,
      },
    },
    panelZoneMap: state.panelZoneMap,
    zoneActivePanels: state.zoneActivePanels,
  };
  state.layoutConfig = layout;
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(layout));
  } catch { /* storage full — silently ignore */ }
}

// ---------------------------------------------------------------------------
// CSS helpers
// ---------------------------------------------------------------------------

function _setCSSProp(prop, valuePx) {
  document.documentElement.style.setProperty(prop, `${valuePx}px`);
}

function _getCSSPropPx(prop) {
  const val = document.documentElement.style.getPropertyValue(prop);
  return val ? parseFloat(val) : 0;
}

// ---------------------------------------------------------------------------
// Apply layout to DOM
// ---------------------------------------------------------------------------

function _applyLayout(layout) {
  const root = document.documentElement;
  const app = refs.app;
  const z = layout.zones;

  // Left zone
  _setCSSProp(CSS_PROP.left, z.left.width);
  state.leftZoneVisible = !z.left.collapsed;
  if (z.left.collapsed && app) {
    app.classList.add('left-collapsed');
  } else if (app) {
    app.classList.remove('left-collapsed');
  }

  // Right zone
  _setCSSProp(CSS_PROP.right, z.right.width);
  state.rightZoneVisible = !z.right.collapsed;
  if (z.right.collapsed && app) {
    app.classList.add('right-collapsed');
  } else if (app) {
    app.classList.remove('right-collapsed');
  }

  // Bottom zone
  _setCSSProp(CSS_PROP.bottom, z.bottom.collapsed ? 0 : z.bottom.height);
  state.bottomZoneVisible = !z.bottom.collapsed;

  // Persist in state
  state.layoutConfig = layout;
}

// ---------------------------------------------------------------------------
// Resize handle logic
// ---------------------------------------------------------------------------

function _initResizeHandles() {
  _initHandle('left');
  _initHandle('right');
  _initHandle('bottom');
}

function _initHandle(zone) {
  const handleId = zone === 'left' ? 'resizeLeft' : zone === 'right' ? 'resizeRight' : 'resizeBottom';
  const handle = refs[handleId];
  if (!handle) return;

  const isVertical = zone === 'bottom';
  const prop = CSS_PROP[zone];
  const limits = LIMITS[zone];

  handle.addEventListener('mousedown', (e) => {
    e.preventDefault();

    // If collapsed, don't allow resizing
    if (zone === 'left' && !state.leftZoneVisible) return;
    if (zone === 'right' && !state.rightZoneVisible) return;
    if (zone === 'bottom' && !state.bottomZoneVisible) return;

    const startPos = isVertical ? e.clientY : e.clientX;
    const startSize = _getCSSPropPx(prop) || (isVertical ? 0 : (zone === 'left' ? DEFAULT_LAYOUT.zones.left.width : DEFAULT_LAYOUT.zones.right.width));

    // Compute max for bottom (50vh)
    const maxSize = isVertical
      ? window.innerHeight * limits.maxVhFraction
      : limits.max;

    document.body.style.cursor = isVertical ? 'row-resize' : 'col-resize';
    document.body.style.userSelect = 'none';

    function onMouseMove(ev) {
      let delta;
      if (isVertical) {
        // Dragging up increases height (row-reverse)
        delta = startPos - ev.clientY;
      } else if (zone === 'right') {
        // Dragging left increases width (col-reverse)
        delta = startPos - ev.clientX;
      } else {
        // Left zone: dragging right increases width
        delta = ev.clientX - startPos;
      }

      const newSize = Math.min(maxSize, Math.max(limits.min, startSize + delta));
      _setCSSProp(prop, newSize);
    }

    function onMouseUp() {
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
      _saveLayout();
    }

    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  });

  // Double-click: reset to default size
  handle.addEventListener('dblclick', () => {
    if (isVertical) {
      _setCSSProp(prop, DEFAULT_LAYOUT.zones.bottom.height);
    } else {
      _setCSSProp(prop, DEFAULT_LAYOUT.zones[zone].width);
    }
    _saveLayout();
  });
}

// ---------------------------------------------------------------------------
// Collapse / Expand / Toggle
// ---------------------------------------------------------------------------

function _updateConsoleToggle() {
  const toggle = refs.sbConsoleToggle;
  if (toggle) toggle.classList.toggle('active', state.bottomZoneVisible);
}

export function collapseZone(zone) {
  const app = refs.app;
  if (zone === 'left') {
    state.leftZoneVisible = false;
    if (app) app.classList.add('left-collapsed');
  } else if (zone === 'right') {
    state.rightZoneVisible = false;
    if (app) app.classList.add('right-collapsed');
  } else if (zone === 'bottom') {
    state.bottomZoneVisible = false;
    _setCSSProp(CSS_PROP.bottom, 0);
  }
  _updateConsoleToggle();
  _saveLayout();
}

export function expandZone(zone) {
  const app = refs.app;
  if (zone === 'left') {
    state.leftZoneVisible = true;
    if (app) app.classList.remove('left-collapsed');
  } else if (zone === 'right') {
    state.rightZoneVisible = true;
    if (app) app.classList.remove('right-collapsed');
  } else if (zone === 'bottom') {
    state.bottomZoneVisible = true;
    // Restore saved height or use a sensible default
    const saved = _loadLayout();
    const h = (saved && saved.zones.bottom.height > 0)
      ? saved.zones.bottom.height
      : 200;
    _setCSSProp(CSS_PROP.bottom, h);
  }
  _updateConsoleToggle();
  _saveLayout();
}

export function toggleZone(zone) {
  if (zone === 'left') {
    state.leftZoneVisible ? collapseZone(zone) : expandZone(zone);
  } else if (zone === 'right') {
    state.rightZoneVisible ? collapseZone(zone) : expandZone(zone);
  } else if (zone === 'bottom') {
    state.bottomZoneVisible ? collapseZone(zone) : expandZone(zone);
  }
}

// ---------------------------------------------------------------------------
// Reset
// ---------------------------------------------------------------------------

export function resetLayout() {
  localStorage.removeItem(STORAGE_KEY);
  state.layoutConfig = null;
  _applyLayout(structuredClone(DEFAULT_LAYOUT));
}

/**
 * Return the loaded/current layout config (including panel zones).
 * Called by main.js to pass saved config into initPanels().
 */
export function getLayoutConfig() {
  return state.layoutConfig;
}

// ---------------------------------------------------------------------------
// Init
// ---------------------------------------------------------------------------

export function initLayout() {
  const saved = _loadLayout();
  const layout = saved || structuredClone(DEFAULT_LAYOUT);
  _applyLayout(layout);
  _initResizeHandles();

  // Wire status bar console toggle
  if (refs.sbConsoleToggle) {
    refs.sbConsoleToggle.addEventListener('click', () => toggleZone('bottom'));
  }

  // Re-save layout when panel-registry signals a panel move
  window.addEventListener('layout-changed', () => _saveLayout());
}
